import type { Dispatch, SetStateAction } from 'react';
import type { QueryClient } from '@tanstack/react-query';
import type { ActiveJob } from '../../components/generation/state/useGenerationState';
import {
  createYouTubeUploadJob,
  waitForJobCompletion,
  type YouTubePrivacyStatus,
  type YouTubeUploadResult,
} from '../../services/asyncJobApi';

interface ToastLike {
  show: (m: string) => void;
  success: (m: string) => void;
  error: (m: string) => void;
}

export interface YouTubeUploadDeps {
  activeJobs: ActiveJob[];
  setActiveJobs: Dispatch<SetStateAction<ActiveJob[]>>;
  setShowJobQueueModal: (v: boolean) => void;
  toast: ToastLike;
  queryClient: QueryClient;
}

export interface YouTubeUploadOptions {
  /** キュー表示用ラベル */
  label: string;
  /** 二重起動防止キー (scenarioId / materialId) */
  dedupeKey: string;
  /** scenario_id (合成) か material_id (単体素材) のいずれかを指定 */
  scenarioId?: string;
  materialId?: string;
  title: string;
  description: string;
  privacy: YouTubePrivacyStatus;
  containsSyntheticMedia: boolean;
  /** progress<50 の段階ラベル (合成/取得)。既定 '動画を生成中' */
  prepStageLabel?: string;
}

/**
 * YouTube アップロードを標準ジョブキューに載せて開始する (非ブロッキング)。
 * シナリオ (compose) と単体素材 (download) の両方で共有。
 * 完了/失敗はキュー(activeJobs)へ反映し、完了項目クリックで YouTube Studio を開ける。
 */
export function startYouTubeUploadJob(deps: YouTubeUploadDeps, opts: YouTubeUploadOptions): void {
  const { activeJobs, setActiveJobs, setShowJobQueueModal, toast, queryClient } = deps;
  const prep = opts.prepStageLabel ?? '動画を生成中';

  // 二重起動防止 (同一対象のアップロードが進行中なら弾く)。
  if (activeJobs.some(j => j.status === 'polling' && j.type === 'youtube-upload' && j.scenarioId === opts.dedupeKey)) {
    toast.show('このYouTubeアップロードは進行中です');
    return;
  }

  void (async () => {
    let job_id: string;
    try {
      ({ job_id } = await createYouTubeUploadJob({
        scenario_id: opts.scenarioId,
        material_id: opts.materialId,
        title: opts.title,
        description: opts.description,
        privacy_status: opts.privacy,
        contains_synthetic_media: opts.containsSyntheticMedia,
        burn_telop: true,
      }));
    } catch (error) {
      console.error('Failed to start YouTube upload:', error);
      toast.error('YouTubeアップロードの開始に失敗しました');
      return;
    }

    setActiveJobs(prev => [...prev, {
      jobId: job_id,
      status: 'polling' as const,
      startedAt: Date.now(),
      type: 'youtube-upload' as const,
      scenarioId: opts.dedupeKey,
      label: opts.label,
      stage: prep,
    }]);
    setShowJobQueueModal(true);

    try {
      const asyncJob = await waitForJobCompletion<YouTubeUploadResult>(job_id, {
        maxDuration: 30 * 60 * 1000,
        skipVisibilityCheck: true,
        // progress>=50 をアップロード段階として段階ラベルを出し分ける。
        onProgress: (job) => {
          const stage = (job.progress ?? 0) >= 50 ? 'YouTubeにアップロード中' : prep;
          setActiveJobs(prev => prev.map(j =>
            j.jobId === job_id && j.status === 'polling' ? { ...j, stage } : j
          ));
        },
      });

      const data = asyncJob.result_data;
      const studioUrl = `https://studio.youtube.com/video/${data.video_id}/edit`;
      setActiveJobs(prev => prev.map(j =>
        j.jobId === job_id ? {
          ...j,
          status: 'completed' as const,
          result: { id: data.video_id, video_id: data.video_id, studio_url: studioUrl },
        } : j
      ));
    } catch (error) {
      console.error('Failed to upload to YouTube:', error);
      const msg = error instanceof Error ? error.message : 'YouTubeアップロードに失敗しました';
      setActiveJobs(prev => prev.map(j =>
        j.jobId === job_id ? { ...j, status: 'failed' as const, error: msg } : j
      ));
      // 401 失効時はサーバーが連携をクリアするため状態を取り直す。
      queryClient.invalidateQueries({ queryKey: ['youtube', 'authStatus'] });
    }
  })();
}
